import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { LuSun, LuMoon, LuScanFace, LuInfo } from 'react-icons/lu';
import { functions } from '../firebase/Init';
import AppLayout from '../components/oracle/AppLayout';
import LogoSpinner from '../components/oracle/LogoSpinner';
import EmptyState from '../components/oracle/EmptyState';
import PageHeader from '../components/oracle/PageHeader';
import RoutineCard from '../components/routine/RoutineCard';
import { ROUTES } from '../contracts/routes';
import { COPY } from '../contracts/copyMap';
import AurumButton from '../components/oracle/AurumButton';
import EntitlementGate from '../components/billing/EntitlementGate';
import UnlockGateModal from '../components/oracle/UnlockGateModal';
import RoutineLockedPreview from '../components/oracle/RoutineLockedPreview';
import RoutinePaywallPanel from '../components/oracle/RoutinePaywallPanel';
import { isPaywalled } from '../contracts/billingApi';
import GlowupRenderModal from '../components/routine/GlowupRenderModal';
import useScanHistory from '../hooks/useScanHistory';
import { ROUTINE_ROUTES } from '../contracts/oracleApi';
import { CONVERSION_EVENTS, useTrackViewOnce } from '../analytics/conversion';

const MAX_STAGE = 6; // progression is 0..6 → "STAGE x / 7"

const isProductItem = (a) => !a.route || a.route === ROUTINE_ROUTES.PRODUCT;

// Responsive card grid within each group: phones get one column; midsize → wide
// screens fan out to more columns. (MORNING / EVENING stack vertically above.)
const CARD_GRID = 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-5 mt-[18px]';

// ROUTINE header. The layout lives in PageHeader; this only binds the copy and
// forwards the slot props, so the ready state can graft on stage progress + the
// efficacy toggle while loading / error / empty render it title-only.
// No rule: the stage meter already separates the header from the content.
const RoutineTitle = (props) => (
  <PageHeader
    title={COPY.ROUTINE_HEADLINE}
    subtitle={COPY.ROUTINE_SUBTITLE}
    rule={false}
    {...props}
  />
);

// Stage progress meter (STAGE x / 7 + bar + actives · price). Shared by the
// desktop (under the rule) and mobile (centered) header layouts.
const StageProgress = ({ stage, activeCount, totalPrice, progressPct, className = '' }) => (
  <div className={`flex items-center gap-[18px] flex-wrap ${className}`}>
    <div className="flex items-center gap-[11px]">
      <span className="font-display text-[11px] tracking-[0.2em] text-bone-dim">
        STAGE {Math.min(stage, MAX_STAGE) + 1} / {MAX_STAGE + 1}
      </span>
      <div className="w-[150px] h-1 rounded-full bg-[rgba(255,255,255,0.1)] overflow-hidden">
        <div
          className="h-full rounded-full"
          style={{ width: `${progressPct}%`, background: 'linear-gradient(90deg, #9A7B40, #E6C083)' }}
        />
      </div>
    </div>
    <span className="font-display text-[15px] text-bone-dim">
      {activeCount} actives · <span className="text-aurum">{totalPrice}</span>
    </span>
  </div>
);

// ── WHERE THE EFFICACY TOGGLE USED TO BE ────────────────────────────────────
//
// Tracking is ON, always, with no control to turn it off. It was a switch whose
// only effect was to hide a read-only readout and suppress the one question the
// scan asks, and defaulting it off for anyone who ever flipped it quietly cost
// them the adherence bonus on every later reading.
//
// The slot it occupied — top-right on desktop, centered on mobile — now carries
// IMAGINE MY GLOWUP, which is the thing a member actually comes to this header
// for. (Previously the CTA sat in `desktopRight` ONLY, so the feature was
// unreachable on a phone.)
//
// The server still stores `routines/{uid}.efficacy_on` and still defaults it to
// true; nothing in the frontend reads or writes it any more, so a stale `false`
// left over from the toggle is inert.
const GlowupCta = ({ onOpen }) => (
  // Pro-only. EntitlementGate renders the paywall inline rather than hiding the
  // feature — a member-only capability the user cannot see cannot convert them.
  <EntitlementGate feature="glowup_render" compact>
    <AurumButton onClick={onOpen} data-testid="glowup-render-cta">
      {COPY.GLOWUP_RENDER_CTA}
    </AurumButton>
  </EntitlementGate>
);

// Page shell — rendered in every state so the page always has a header
// (loading / error / empty), matching VAULT / CREATE. The header is hoisted
// into AppLayout (sticky, condenses on scroll); pass `header` to override the
// default title-only header.
const RoutineShell = ({ children, header }) => (
  <AppLayout header={header || <RoutineTitle />}>
    <div className="px-5 lg:px-12 pb-10 lg:pb-14">
      {children}
    </div>
  </AppLayout>
);

const GroupHeader = ({ icon, label }) => (
  <div className="flex items-center gap-[11px] pb-3.5 border-b border-hairline-soft">
    <span className="text-aurum flex shrink-0" aria-hidden="true">{icon}</span>
    <span className="font-display uppercase text-xs tracking-[0.24em] text-[#E8E2D6]">{label}</span>
  </div>
);

const Routine = () => {
  const navigate = useNavigate();
  const [state, setState] = useState({ phase: 'loading' });
  const [renderOpen, setRenderOpen] = useState(false);
  // The paywall modal opens itself on arrival and can be dismissed back to the
  // sneak peek. Declared up here with the other hooks because the paywalled
  // branch is an early return.
  const [gateOpen, setGateOpen] = useState(true);
  // The BEFORE pane of the render comparison. The thumbnail is enough at this
  // size and is already signed by listVaultEntries — a sealed reading still
  // carries one, so the comparison works even before the scan is unlocked.
  const { latest: lastScan } = useScanHistory({ limit: 1 });
  const lastScanImageUrl = (lastScan && lastScan.thumbnail_url) || null;
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const load = useCallback(async () => {
    setState({ phase: 'loading' });
    try {
      const getRoutine = httpsCallable(functions, 'getRoutine');
      const result = await getRoutine({});
      const data = result && result.data;
      if (!mountedRef.current) return;
      if (data && data.status === 'success') {
        setState({ phase: 'ready', data });
      } else if (isPaywalled(data)) {
        // Distinct from `error` on purpose. Everything else here is "something
        // broke, try again"; this is "this is held for members", and showing a
        // retry button for it is both wrong and slightly insulting — retrying
        // will fail identically forever.
        //
        // Driven by the SERVER's answer rather than the client entitlement doc,
        // which is what makes it authoritative: a stale or unread
        // entitlements/{uid} snapshot cannot show someone a paywall they have
        // already paid past, nor hide one they have not.
        setState({ phase: 'paywalled' });
      } else {
        setState({ phase: 'error', message: COPY.REJECT_NETWORK_SUBTITLE });
      }
    } catch (err) {
      if (mountedRef.current) {
        setState({ phase: 'error', message: (err && err.message) || COPY.REJECT_NETWORK_SUBTITLE });
      }
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Conversion funnel: one view per visit, stamped with how the page resolved.
  // Waits for the phase to settle because `locked` is the number that matters —
  // routine views split by paywalled-or-not is the routine half of the
  // membership conversion story.
  useTrackViewOnce(
    CONVERSION_EVENTS.ROUTINE_SCREEN_VIEWED,
    { phase: state.phase, locked: state.phase === 'paywalled' },
    state.phase !== 'loading',
  );

  // Optimistic adherence write — flips the local active immediately, persists,
  // and reverts on failure.
  const setAdherence = useCallback((activeId, value) => {
    setState((s) => {
      if (s.phase !== 'ready') return s;
      const apply = (list) => list.map((a) => (a.active_id === activeId ? { ...a, adherence: value } : a));
      return { ...s, data: { ...s.data, am: apply(s.data.am), pm: apply(s.data.pm) } };
    });
    const call = httpsCallable(functions, 'setAdherence');
    call({ active_id: activeId, value }).catch(() => load());
  }, [load]);

  if (state.phase === 'loading') {
    return (
      <RoutineShell>
        <div className="flex items-center justify-center py-24"><LogoSpinner /></div>
      </RoutineShell>
    );
  }

  if (state.phase === 'paywalled') {
    // The regimen behind glass. The blur is decoration over SYNTHETIC geometry
    // — the real enforcement is getRoutine returning ENTITLEMENT_REQUIRED with
    // no payload, so there is nothing here to peel the filter off of. See the
    // header of RoutineLockedPreview.
    //
    // showJudgePath={false} because judging opens the reading, never the
    // regimen (CLAUDE.md rule 2). Offering it here would promise an unlock the
    // backend does not implement.
    //
    // NOT NOW dismisses to the sneak peek — the ROUTINE page with its own
    // header, the stage rail and the card grid all present but blurred — and
    // NOT to /home. Ejecting someone off the page they asked for reads as a
    // bounce; leaving them on it with the shape of the thing visible is the
    // whole argument for membership.
    //
    // RoutinePaywallPanel is the standing offer in that state. It carries its
    // own scrim because a transparent card disappears into the blurred tiles
    // behind it — see its header.
    // The peek is CROPPED to roughly one screen, and that is load-bearing for
    // the panel's centring. RoutineLockedPreview renders the full page — two
    // card grids, guidance, the disclaimer — so it runs thousands of pixels
    // tall. The panel centres inside this box, so an uncropped box would park
    // the offer halfway down a page nobody can read and leave the first screen
    // empty. Cropping keeps the card centred in view and still shows a full
    // grid's worth of regimen behind it.
    return (
      <RoutineShell>
        {/* FULL-BLEED, and that is the whole point of the negative margins.
            The scrim lives on this box, so any gap between the box and the
            viewport draws the scrim as a visible rectangle sitting on the page
            background — a hard border around the peek. Cancelling RoutineShell's
            gutters and bottom padding pushes the dim to the content edges, so
            it reads as the page continuing behind the card instead of a panel
            pasted onto it. `h-[86vh]` (not max-h) keeps the bottom edge below
            the fold for the same reason.

            The preview keeps the gutters INSIDE, so the blurred cards still sit
            on the page grid rather than running off the edge. */}
        <div
          className="relative -mx-5 -mb-10 h-[86vh] min-h-[540px] overflow-hidden lg:-mx-12 lg:-mb-14"
          data-testid="routine-peek"
        >
          <div className="px-5 lg:px-12">
            <RoutineLockedPreview />
          </div>
          {!gateOpen && <RoutinePaywallPanel />}
          <UnlockGateModal
            open={gateOpen}
            surface="routine"
            showJudgePath={false}
            headline={COPY.UNLOCK_GATE_HEADLINE_ROUTINE}
            subcopy={COPY.UNLOCK_GATE_BODY_ROUTINE}
            onDismiss={() => setGateOpen(false)}
          />
        </div>
      </RoutineShell>
    );
  }

  if (state.phase === 'error') {
    return (
      <RoutineShell>
        <div className="flex items-center justify-center py-16">
          <EmptyState
            headline="YOUR ROUTINE IS UNREACHABLE"
            subtitle={state.message}
            ctaLabel={COPY.TRY_AGAIN_CTA}
            onCta={load}
          />
        </div>
      </RoutineShell>
    );
  }

  const { am = [], pm = [], total_price: totalPrice = '$0', stage = 0 } = state.data;
  const amProducts = am.filter(isProductItem);
  const pmProducts = pm.filter(isProductItem);
  // Clinician / behavioral cards ride in am/pm but render in their own section.
  const advisories = [...am, ...pm].filter((a) => !isProductItem(a));
  const activeCount = amProducts.length + pmProducts.length;
  const totalCards = am.length + pm.length;

  if (totalCards === 0) {
    return (
      <RoutineShell>
        <div className="flex items-center justify-center py-16">
          <EmptyState
            headline={COPY.ROUTINE_EMPTY_HEADLINE}
            subtitle={COPY.ROUTINE_EMPTY_SUBTITLE}
            ctaLabel="CAPTURE A SCAN"
            onCta={() => navigate(ROUTES.CREATE)}
            icon={<LuScanFace size={22} />}
          />
        </div>
      </RoutineShell>
    );
  }

  const progressPct = Math.round((Math.min(stage, MAX_STAGE) / MAX_STAGE) * 100);

  const progressProps = { stage, activeCount, totalPrice, progressPct };
  const readyHeader = (
    <RoutineTitle
      desktopExtra={(
        <>
          <div className="w-[72px] h-[2px] bg-[#3c3b38] mt-5" />
          <StageProgress {...progressProps} className="mt-[18px]" />
        </>
      )}
      desktopRight={(
        <div className="mt-1.5 flex flex-col items-end gap-3">
          <GlowupCta onOpen={() => setRenderOpen(true)} />
        </div>
      )}
      mobileExtra={(
        <>
          <StageProgress {...progressProps} className="mt-4 justify-center" />
          <div className="mt-4 flex justify-center">
            <GlowupCta onOpen={() => setRenderOpen(true)} />
          </div>
        </>
      )}
    />
  );

  return (
    <RoutineShell header={readyHeader}>
        {/* MORNING then EVENING, stacked vertically; cards flow into a
            responsive grid (1 col on phones, more on midsize → wide screens). */}
        <div className="mt-8 lg:mt-[34px]">
          <GroupHeader icon={<LuSun size={17} strokeWidth={1.5} />} label="MORNING" />
          <div className={CARD_GRID}>
            {amProducts.length === 0 ? (
              <p className="font-display italic text-sm text-bone-dim m-0">No morning steps.</p>
            ) : (
              amProducts.map((a) => (
                <RoutineCard key={a.active_id} active={a} onAdherence={setAdherence} />
              ))
            )}
          </div>
        </div>
        <div className="mt-8 lg:mt-[34px]">
          <GroupHeader icon={<LuMoon size={17} strokeWidth={1.5} />} label="EVENING" />
          <div className={CARD_GRID}>
            {pmProducts.length === 0 ? (
              <p className="font-display italic text-sm text-bone-dim m-0">No evening steps.</p>
            ) : (
              pmProducts.map((a) => (
                <RoutineCard key={a.active_id} active={a} onAdherence={setAdherence} />
              ))
            )}
          </div>
        </div>

        {/* clinician / behavioral guidance — no products */}
        {advisories.length > 0 && (
          <div className="mt-8 lg:mt-[34px]">
            <GroupHeader icon={<LuInfo size={17} strokeWidth={1.5} />} label="GUIDANCE" />
            <div className={CARD_GRID}>
              {advisories.map((a) => (
                <RoutineCard key={a.active_id} active={a} />
              ))}
            </div>
          </div>
        )}

        {/* standing disclaimer */}
        <div className="flex items-start gap-[13px] mt-[30px] px-[22px] py-[18px] bg-[rgba(255,255,255,0.03)] border border-hairline-soft rounded-field">
          <span className="text-bone-dim shrink-0 mt-px" aria-hidden="true">
            <LuInfo size={18} strokeWidth={1.5} />
          </span>
          <span className="font-display text-sm leading-relaxed text-bone-dim">
            {COPY.ROUTINE_DISCLAIMER}
          </span>
        </div>

        {/* `beforeScore` is null on a SEALED reading (listVaultEntries withholds
            it), which is exactly when the projection panel must not draw — see
            the modal. The product actives drive WHAT'S DRIVING THIS and, via
            their stored adherence, the projection itself. */}
        <GlowupRenderModal
          open={renderOpen}
          beforeUrl={lastScanImageUrl}
          beforeScore={lastScan ? lastScan.overall_score : null}
          actives={[...amProducts, ...pmProducts]}
          onClose={() => setRenderOpen(false)}
        />
    </RoutineShell>
  );
};

export default Routine;
